import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ShieldCheck, Eye, Send, Square, Lock } from 'lucide-react';
import surveyApi from '../surveyApi';
import SurveyQuestionEditor from './SurveyQuestionEditor';
import SurveyRoundsPanel from './SurveyRoundsPanel';
import SurveyTranslationsPanel from './SurveyTranslationsPanel';

// Render any stored instant (UTC ISO from the server, or a local datetime-local
// string after the admin edits it) as the admin's local wall-clock for the input.
const toLocalInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
// Convert the value held in state (local datetime-local string, or server ISO) to a
// UTC MySQL DATETIME string so the stored instant matches what the admin intended.
const toUtcMysql = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 19).replace('T', ' ');
};

export default function SurveyEditor() {
  const { surveyId } = useParams();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [audienceRoleIds, setAudienceRoleIds] = useState([]);
  const [roles, setRoles] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [responseCount, setResponseCount] = useState(0);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [data, opts] = await Promise.all([
        surveyApi.adminGet(surveyId),
        surveyApi.adminAudienceOptions(),
      ]);
      setSurvey(data.survey);
      setQuestions(data.questions);
      setAudienceRoleIds(data.audience_role_ids || []);
      setResponseCount(data.response_count || 0);
      setRounds(data.rounds || []);
      setRoles(opts.roles || []);
      setTrainings(opts.trainings || []);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  }, [surveyId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setSurvey((s) => ({ ...s, [k]: v }));
  const toggleRole = (id) =>
    setAudienceRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    try {
      setSaving(true);
      await surveyApi.adminUpdate(surveyId, {
        title: survey.title,
        description: survey.description,
        instructions_md: survey.instructions_md,
        thank_you_md: survey.thank_you_md,
        status: survey.status,
        is_anonymous: !!survey.is_anonymous,
        allow_multiple_responses: !!survey.allow_multiple_responses,
        randomize_questions: !!survey.randomize_questions,
        show_progress: !!survey.show_progress,
        opens_at: toUtcMysql(survey.opens_at),
        closes_at: toUtcMysql(survey.closes_at),
        audience_mode: survey.audience_mode,
        audience_role_ids: audienceRoleIds,
        related_training_id: survey.related_training_id || null,
      });
      setOkMsg('Saved.');
      setTimeout(() => setOkMsg(''), 2000);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      setSaving(true);
      await surveyApi.adminUpdate(surveyId, { status });
      setField('status', status);
      setOkMsg(status === 'open' ? 'Published.' : status === 'closed' ? 'Closed.' : 'Saved.');
      setTimeout(() => setOkMsg(''), 2000);
    } catch (e) {
      setError(e.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (error && !survey) return <div className="p-8 text-rose-300">{error}</div>;
  if (!survey) return null;

  const questionCount = questions.filter((q) => q.question_type !== 'section').length;

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <button onClick={() => navigate('/tiles/ksp_training/admin/surveys')}
          className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Surveys
        </button>
        <div className="flex items-center gap-2">
          {okMsg && <span className="text-emerald-300 text-sm">{okMsg}</span>}
          <button onClick={() => window.open(`/tiles/ksp_training/survey/${surveyId}?preview=1`, '_blank', 'noopener')}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm flex items-center gap-1">
            <Eye className="w-4 h-4" /> Preview
          </button>
          {survey.status === 'open' ? (
            <button onClick={() => setStatus('closed')} disabled={saving}
              className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-1 disabled:opacity-60">
              <Square className="w-4 h-4" /> Close
            </button>
          ) : (
            <button onClick={() => setStatus('open')} disabled={saving || questionCount === 0}
              title={questionCount === 0 ? 'Add a question before publishing' : 'Publish — visible to the audience'}
              className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-1 disabled:opacity-40">
              <Send className="w-4 h-4" /> Publish
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>}

      {responseCount > 0 && (
        <div className="mb-4 p-3 rounded bg-sky-900/20 border border-sky-700 text-sky-200 text-sm flex items-center gap-2">
          <Lock className="w-4 h-4 shrink-0" />
          This survey has {responseCount} response{responseCount === 1 ? '' : 's'}. To protect the data, answered
          questions are locked — you can still edit their wording, but not their type or options.
        </div>
      )}

      <div className="rounded-lg border border-slate-700 bg-slate-800 p-4 mb-6 space-y-4">
        <Field label="Title">
          <input value={survey.title || ''} onChange={(e) => setField('title', e.target.value)}
            className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
        </Field>
        <Field label="Description">
          <textarea rows={2} value={survey.description || ''} onChange={(e) => setField('description', e.target.value)}
            className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Intro / instructions (shown before the survey)">
            <textarea rows={3} value={survey.instructions_md || ''} onChange={(e) => setField('instructions_md', e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
          </Field>
          <Field label="Thank-you message (shown after submit)">
            <textarea rows={3} value={survey.thank_you_md || ''} onChange={(e) => setField('thank_you_md', e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Status">
            <select value={survey.status} onChange={(e) => setField('status', e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100">
              <option value="draft">Draft (hidden)</option>
              <option value="open">Open (collecting responses)</option>
              <option value="closed">Closed (no new responses)</option>
            </select>
          </Field>
          <Field label="Linked training (optional — surfaced after that course)">
            <select value={survey.related_training_id || ''} onChange={(e) => setField('related_training_id', e.target.value || null)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100">
              <option value="">None</option>
              {trainings.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </Field>
          <Field label="Opens at (optional)">
            <input type="datetime-local" value={toLocalInput(survey.opens_at)}
              onChange={(e) => setField('opens_at', e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
          </Field>
          <Field label="Closes at (optional)">
            <input type="datetime-local" value={toLocalInput(survey.closes_at)}
              onChange={(e) => setField('closes_at', e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
          <Toggle checked={!!survey.is_anonymous} onChange={(v) => setField('is_anonymous', v)}
            disabled={responseCount > 0}
            title={responseCount > 0 ? 'Locked because responses exist' : undefined}
            label={responseCount > 0
              ? 'Anonymous (locked — responses exist)'
              : 'Anonymous (responses are aggregate-only, never named)'}
            icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />} />
          <Toggle checked={!!survey.allow_multiple_responses} onChange={(v) => setField('allow_multiple_responses', v)}
            label="Allow multiple responses per person" />
          <Toggle checked={!!survey.randomize_questions} onChange={(v) => setField('randomize_questions', v)}
            label="Randomize question order" />
          <Toggle checked={!!survey.show_progress} onChange={(v) => setField('show_progress', v)}
            label="Show progress bar to respondents" />
        </div>

        <div className="pt-1">
          <Field label="Audience">
            <select value={survey.audience_mode} onChange={(e) => setField('audience_mode', e.target.value)}
              className="w-full md:w-72 px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100">
              <option value="all">Everyone with training access</option>
              <option value="roles">Specific roles only</option>
            </select>
          </Field>
          {survey.audience_mode === 'roles' && (
            <div className="mt-2 flex flex-wrap gap-2">
              {roles.map((r) => {
                const on = audienceRoleIds.includes(r.id);
                return (
                  <button key={r.id} type="button" onClick={() => toggleRole(r.id)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      on ? 'border-amber-500 bg-amber-900/30 text-amber-100' : 'border-slate-700 text-slate-300 hover:bg-slate-700/40'}`}>
                    {r.name}
                  </button>
                );
              })}
              {roles.length === 0 && <span className="text-sm text-slate-400">No roles found.</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={saving}
            className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
          {okMsg && <span className="text-emerald-300 text-sm">{okMsg}</span>}
        </div>
      </div>

      <SurveyRoundsPanel surveyId={survey.id} rounds={rounds} status={survey.status} onChange={load} />

      <SurveyQuestionEditor surveyId={survey.id} questions={questions} responseCount={responseCount} onChange={load} />

      <SurveyTranslationsPanel survey={survey} questions={questions} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, icon, disabled, title }) {
  return (
    <label title={title}
      className={`inline-flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <input type="checkbox" className="accent-amber-500" checked={checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)} />
      {icon}
      <span className="text-sm text-slate-200">{label}</span>
    </label>
  );
}
